import React from 'react';
import { Twitter, Facebook, Linkedin, Instagram, Youtube, Music2, Globe } from 'lucide-react';
import { SocialMediaLink, EventStyle } from '@portal/types/eventDetails.types';

interface SocialMediaFooterProps {
  socialMediaLinks: SocialMediaLink[];
  eventName: string;
  style?: EventStyle;
  className?: string;
}

// Get the appropriate icon for each social media platform
const getSocialMediaIcon = (platform: string) => {
  switch (platform.toLowerCase()) {
    case 'twitter':
      return Twitter;
    case 'facebook':
      return Facebook;
    case 'linkedin':
      return Linkedin;
    case 'instagram':
      return Instagram;
    case 'youtube':
      return Youtube;
    case 'tiktok':
      return Music2;
    case 'website':
      return Globe;
    default:
      return null;
  }
};

// Get display name for platform
const getPlatformDisplayName = (platform: string) => {
  switch (platform.toLowerCase()) {
    case 'linkedin':
      return 'LinkedIn';
    case 'facebook':
      return 'Facebook';
    case 'instagram':
      return 'Instagram';
    case 'twitter':
      return 'X (Twitter)';
    case 'tiktok':
      return 'TikTok';
    case 'youtube':
      return 'YouTube';
    case 'website':
      return 'Website';
    default:
      return platform;
  }
};

export const SocialMediaFooter: React.FC<SocialMediaFooterProps> = ({
  socialMediaLinks,
  eventName,
  style,
  className = ''
}) => {
  // Don't render if no links provided
  if (!socialMediaLinks || socialMediaLinks.length === 0) {
    return null;
  }

  // Filter out links that don't have URLs
  const activeLinks = socialMediaLinks.filter(link => link.url);

  // Generate background style
  const getBackgroundStyle = () => {
    if (style?.headerColor) {
      return {
        background: `linear-gradient(135deg, ${style.headerColor}f0, ${style.headerColor}f7, ${style.headerColor}f0)`
      };
    }
    // Default gradient with 3% transparency (97% opaque)
    return {
      background: 'linear-gradient(135deg, #1e293bf7, #334155f5, #475569f2)'
    };
  };

  return (
    <div
      className={`w-full ${className}`}
      style={getBackgroundStyle()}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* Social media icons */}
            <div className="flex items-center gap-6 flex-wrap justify-center">
              {activeLinks.map((link, index) => {
                const Icon = getSocialMediaIcon(link.platform);
                if (!Icon) return null;

                return (
                  <a
                    key={link.id || `${link.platform}-${index}`}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2 text-white/80 hover:text-white transition-all duration-200"
                    aria-label={`Visit our ${getPlatformDisplayName(link.platform)} page`}
                  >
                    <div className="p-2 rounded-full bg-white/10 group-hover:bg-white/20 transition-colors duration-200">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="hidden sm:inline-block text-sm font-medium">
                      {getPlatformDisplayName(link.platform)}
                    </span>
                  </a>
                );
              })}
            </div>
          </div>

          {/* Optional: Show event name */}
          {eventName && (
            <div className="mt-3 text-center">
              <p className="text-xs text-white/60">
                Connect with {eventName}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};